import { ChangeDetectionStrategy, Component, Inject, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';

import { ParsedCsv } from '../../services/csv-parsing.service';
import { CsvParsingService } from '../../services/csv-parsing.service';
import {
  ColumnMapping,
  ImportSchema,
  ImportSchemaField,
  ImportSchemaGroup,
  SENTINEL_UNMAPPED,
} from '../../interfaces/import-schema.interface';

export interface ColumnMappingModalData {
  readonly schema: ImportSchema;
  readonly parsedCsv: ParsedCsv;
}

export interface ColumnMappingResult {
  readonly mapping: ColumnMapping;
}

interface GroupedField {
  readonly field: EnrichedImportSchemaField;
  readonly controlName: string;
}

interface FieldGroupView {
  readonly group: ImportSchemaGroup | null;
  readonly groupLabel: string; // Precalculated label
  readonly fields: ReadonlyArray<GroupedField>;
}

/** Extended field with precalculated i18n data to avoid expensive calls in template. */
interface EnrichedImportSchemaField extends ImportSchemaField {
  readonly label: string; // Precalculated with i18n fallback
  readonly help: string | null; // Precalculated with i18n fallback
  readonly enumAcceptedText: string | null; // Precalculated for enum fields
}

const PREVIEW_ROW_COUNT = 2;

/** Max enum values listed inline before collapsing with a "+N more" suffix. */
const MAX_ENUM_VALUES_SHOWN = 4;

/** sessionStorage flag so the enum banner stays dismissed across modal re-opens within the same tab session. */
const ENUM_BANNER_DISMISSED_KEY = 'bulkUpload.columnMapping.enumBanner.dismissed';

@Component({
  selector: 'app-column-mapping-modal',
  templateUrl: './column-mapping-modal.component.html',
  styleUrls: ['./column-mapping-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ColumnMappingModalComponent implements OnInit {
  readonly unmappedSentinel = SENTINEL_UNMAPPED;

  readonly form: FormGroup;

  readonly groupedFields: ReadonlyArray<FieldGroupView>;

  readonly csvHeaders: ReadonlyArray<string>;

  readonly hasEnumFields: boolean;

  bannerDismissed: boolean;

  private readonly criticalControlNames: ReadonlyArray<string>;

  private readonly columnPreviewCache = new Map<string, ReadonlyArray<string>>();

  private readonly enumValueSetCache = new Map<string, ReadonlySet<string>>();

  private readonly enrichedFields: Map<string, EnrichedImportSchemaField> = new Map();

  constructor(
    private readonly fb: FormBuilder,
    private readonly csvParsing: CsvParsingService,
    private readonly translate: TranslateService,
    private readonly dialogRef: MatDialogRef<ColumnMappingModalComponent, ColumnMappingResult | null>,
    @Inject(MAT_DIALOG_DATA) private readonly data: ColumnMappingModalData,
  ) {
    this.csvHeaders = data.parsedCsv.headers;
    this.enrichedFields = this.buildEnrichedFields(data.schema.fields);
    this.groupedFields = this.buildGroupedFields(data.schema);
    this.criticalControlNames = data.schema.fields
      .filter(f => f.critical)
      .map(f => this.toControlName(f.property));
    this.form = this.buildForm(data.schema.fields);
    this.hasEnumFields = data.schema.fields.some(
      f => f.type === 'enum' && !!f.enumValues && f.enumValues.length > 0,
    );
    this.bannerDismissed = this.readBannerDismissed();
  }

  ngOnInit(): void {
    this.applyAutoMatch();
  }

  getSelectedHeader(property: string): string {
    const control = this.form.get(this.toControlName(property));
    return control?.value ?? this.unmappedSentinel;
  }

  getPreviewForProperty(property: string): ReadonlyArray<string> {
    const header = this.getSelectedHeader(property);
    if (!header || header === this.unmappedSentinel) return [];
    return this.getColumnPreview(header);
  }

  get missingCriticalCount(): number {
    return this.criticalControlNames.reduce((acc, name) => {
      const value = this.form.get(name)?.value;
      return acc + (value === this.unmappedSentinel || value == null ? 1 : 0);
    }, 0);
  }

  get canConfirm(): boolean {
    return this.missingCriticalCount === 0;
  }

  cancel(): void {
    this.dialogRef.close(null);
  }

  confirm(): void {
    if (!this.canConfirm) return;
    const rawValue = this.form.getRawValue() as Record<string, string>;
    const mapping = this.fromFormValues(rawValue);
    this.dialogRef.close({ mapping });
  }

  trackByGroupKey = (_index: number, group: FieldGroupView): string =>
    group.group?.key ?? '__ungrouped__';

  trackByControlName = (_index: number, item: GroupedField): string => item.controlName;

  trackByHeader = (_index: number, header: string): string => header;

  // i18n: prefer a translation keyed by the property name and fall back to the
  // label/help/group.label the backend already sent. The fallback keeps the
  // modal readable even if a new field is added in the backend before the
  // translation file is updated.
  getFieldLabel(field: ImportSchemaField): string {
    // Use precalculated label from enriched field
    return (field as EnrichedImportSchemaField).label || field.label;
  }

  getFieldHelp(field: ImportSchemaField): string | null {
    // Use precalculated help from enriched field
    return (field as EnrichedImportSchemaField).help ?? null;
  }

  getGroupLabel(group: ImportSchemaGroup): string {
    // Find the group in groupedFields to get precalculated label
    for (const view of this.groupedFields) {
      if (view.group?.key === group.key) {
        return view.groupLabel;
      }
    }
    return group.label;
  }

  // Non-invasive text shown under each enum mat-select reminding the user
  // which backend-accepted values are valid. Returns null for non-enum fields
  // so the template can skip rendering.
  getEnumAcceptedText(field: ImportSchemaField): string | null {
    // Use precalculated enum text from enriched field
    return (field as EnrichedImportSchemaField).enumAcceptedText ?? null;
  }

  // Preview helper used by the template to paint cells red when the CSV value
  // mapped to an enum field is not one of the accepted codes. Comparison is
  // case-insensitive to be forgiving on casing but the import itself still
  // requires an exact match — this is purely a visual early-warning.
  isInvalidEnumCell(field: ImportSchemaField, cellValue: string): boolean {
    if (field.type !== 'enum' || !field.enumValues || field.enumValues.length === 0) {
      return false;
    }
    const trimmed = cellValue.trim();
    if (trimmed.length === 0) return false;
    return !this.getEnumValueSet(field).has(trimmed.toLowerCase());
  }

  onBannerDismiss(): void {
    this.bannerDismissed = true;
    this.writeBannerDismissed();
  }

  private buildForm(fields: ReadonlyArray<ImportSchemaField>): FormGroup {
    const controls: Record<string, FormControl<string>> = {};
    for (const field of fields) {
      const initial = this.unmappedSentinel;
      const validators = field.critical
        ? [Validators.required, this.mappedValidator()]
        : [];
      controls[this.toControlName(field.property)] = this.fb.nonNullable.control(
        initial,
        { validators },
      );
    }
    return this.fb.group(controls);
  }

  /** Precalculate all i18n and derived data for fields once during construction. */
  private buildEnrichedFields(fields: ReadonlyArray<ImportSchemaField>): Map<string, EnrichedImportSchemaField> {
    const enriched = new Map<string, EnrichedImportSchemaField>();
    for (const field of fields) {
      enriched.set(field.property, {
        ...field,
        label: this.precalculateFieldLabel(field),
        help: this.precalculateFieldHelp(field),
        enumAcceptedText: this.precalculateEnumAcceptedText(field),
      });
    }
    return enriched;
  }

  private precalculateFieldLabel(field: ImportSchemaField): string {
    const key = `pages.stock.bulkUpload.schema.fields.${field.property}.label`;
    const translated = this.translate.instant(key);
    return translated === key ? field.label : translated;
  }

  private precalculateFieldHelp(field: ImportSchemaField): string | null {
    if (!field.help) return null;
    const key = `pages.stock.bulkUpload.schema.fields.${field.property}.help`;
    const translated = this.translate.instant(key);
    return translated !== key ? translated : field.help;
  }

  private precalculateGroupLabel(group: ImportSchemaGroup): string {
    const key = `pages.stock.bulkUpload.schema.groups.${group.key}.label`;
    const translated = this.translate.instant(key);
    return translated === key ? group.label : translated;
  }

  private precalculateEnumAcceptedText(field: ImportSchemaField): string | null {
    if (field.type !== 'enum' || !field.enumValues || field.enumValues.length === 0) {
      return null;
    }
    const values = field.enumValues.map(e => e.value);
    if (values.length <= MAX_ENUM_VALUES_SHOWN) {
      return this.translate.instant(
        'pages.stock.bulkUpload.columnMapping.enumAccepted',
        { values: values.join(', ') },
      );
    }
    const shown = values.slice(0, MAX_ENUM_VALUES_SHOWN).join(', ');
    const more = values.length - MAX_ENUM_VALUES_SHOWN;
    return this.translate.instant(
      'pages.stock.bulkUpload.columnMapping.enumAcceptedMore',
      { values: shown, more },
    );
  }

  private mappedValidator(): ValidatorFn {
    const sentinel = this.unmappedSentinel;
    return (control: AbstractControl): ValidationErrors | null =>
      control.value === sentinel ? { unmapped: true } : null;
  }

  private buildGroupedFields(schema: ImportSchema): ReadonlyArray<FieldGroupView> {
    const byGroupKey = new Map<string | null, GroupedField[]>();
    for (const field of schema.fields) {
      const groupKey = field.group ?? null;
      if (!byGroupKey.has(groupKey)) byGroupKey.set(groupKey, []);
      const enrichedField = this.enrichedFields.get(field.property);
      if (enrichedField) {
        byGroupKey.get(groupKey)!.push({
          field: enrichedField,
          controlName: this.toControlName(field.property),
        });
      }
    }

    const views: FieldGroupView[] = [];
    for (const group of schema.groups) {
      const fields = byGroupKey.get(group.key);
      if (fields && fields.length > 0) {
        views.push({
          group,
          groupLabel: this.precalculateGroupLabel(group),
          fields
        });
        byGroupKey.delete(group.key);
      }
    }
    const ungrouped = byGroupKey.get(null);
    if (ungrouped && ungrouped.length > 0) {
      views.push({ group: null, groupLabel: '', fields: ungrouped });
    }
    return views;
  }

  private applyAutoMatch(): void {
    const normalizedCsvHeaders = new Map<string, string>();
    for (const header of this.csvHeaders) {
      normalizedCsvHeaders.set(this.csvParsing.normalizeHeader(header), header);
    }

    for (const field of this.data.schema.fields) {
      const controlName = this.toControlName(field.property);
      const match = this.findMatchingHeader(field, normalizedCsvHeaders);
      if (match) {
        this.form.get(controlName)?.setValue(match);
      }
    }
  }

  private findMatchingHeader(
    field: ImportSchemaField,
    normalizedCsvHeaders: Map<string, string>,
  ): string | null {
    for (const alias of field.aliases) {
      const normalizedAlias = this.csvParsing.normalizeHeader(alias);
      const csvHeader = normalizedCsvHeaders.get(normalizedAlias);
      if (csvHeader) return csvHeader;
    }
    const propertyNormalized = this.csvParsing.normalizeHeader(field.property);
    return normalizedCsvHeaders.get(propertyNormalized) ?? null;
  }

  private getColumnPreview(header: string): ReadonlyArray<string> {
    const cached = this.columnPreviewCache.get(header);
    if (cached) return cached;

    const headerIndex = this.csvHeaders.indexOf(header);
    if (headerIndex === -1) {
      this.columnPreviewCache.set(header, []);
      return [];
    }

    const preview: string[] = [];
    for (const row of this.data.parsedCsv.rows) {
      if (preview.length >= PREVIEW_ROW_COUNT) break;
      const value = row[headerIndex]?.trim() ?? '';
      if (value.length > 0) preview.push(value);
    }
    this.columnPreviewCache.set(header, preview);
    return preview;
  }

  private fromFormValues(raw: Record<string, string>): ColumnMapping {
    const mapping: Record<string, string> = {};
    for (const [controlName, value] of Object.entries(raw)) {
      mapping[this.fromControlName(controlName)] = value;
    }
    return mapping;
  }

  private toControlName(property: string): string {
    return `field_${property}`;
  }

  private fromControlName(controlName: string): string {
    return controlName.startsWith('field_') ? controlName.slice('field_'.length) : controlName;
  }

  private getEnumValueSet(field: ImportSchemaField): ReadonlySet<string> {
    const cached = this.enumValueSetCache.get(field.property);
    if (cached) return cached;
    const set = new Set<string>(
      (field.enumValues ?? []).map(e => e.value.toLowerCase()),
    );
    this.enumValueSetCache.set(field.property, set);
    return set;
  }

  // sessionStorage is the right scope here: the reminder is useful once per
  // session but the user shouldn't have to dismiss it every time they reopen
  // the modal in the same tab. It intentionally does NOT persist across tabs.
  private readBannerDismissed(): boolean {
    try {
      return sessionStorage.getItem(ENUM_BANNER_DISMISSED_KEY) === '1';
    } catch {
      return false;
    }
  }

  private writeBannerDismissed(): void {
    try {
      sessionStorage.setItem(ENUM_BANNER_DISMISSED_KEY, '1');
    } catch {
      // Storage can throw in private-mode or sandboxed iframes — the banner
      // simply won't persist; the component keeps working in-memory.
    }
  }
}
